import * as Sentry from "@sentry/browser";
import _ from "lodash";
import { useSnackbar } from "notistack";
import React from "react";
import { useHistory, useLocation, Redirect } from "react-router-dom";
import { useRecoilCallback, useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";

import { RegisteredStatusEnum, User } from "@/api/generated";
import { PROPERTY_TYPE_VALUES } from "@/api/mappings/property";
import { AppLoadError, LoadFailure } from "@/components/AppLoadError";
import { Loading } from "@/components/Loading";
import { PropallyApiFactory } from "@/module/CustomApiFactory";
import {
  AppEventTokenEnum,
  clearAppBadge,
  sendEventToAppMeasurementTool,
  setAppBadgeCount,
} from "@/utilities/capacitorLogic";
import { NotifyNetworkError } from "@/utilities/NotifyNetworkError";
import {
  FetchedPropertyHolders,
  HasUnreadAnnouncementState,
  PropertyHolder,
  PropertyHoldersState,
  PropertyIds,
  RequireLoadingFromBackendState,
  SelectedPropertyId,
  SelectedPropertyIds,
  SessionState,
  UserDataFirstRegisteredStatusState,
  UserState,
} from "@/view_models/atoms";
import { BackendLoadedState } from "@/view_models/atoms";
import { assessmentUnreadCount, investmentUnreadCount } from "@/view_models/proposalUnreadCounts";

import NoImage from "@assets/image/NoImage.svg";

import { ConsentModals } from "./ConsentModals";

export const PropallyRecoilRoot: React.FC<React.PropsWithChildren<any>> = (props) => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [initialLoading, setInitialLoading] = React.useState(true);
  const loadedOnce = React.useRef(false);
  const [loadFailure, setLoadFailure] = React.useState<LoadFailure | null>(null);
  const [sessionExpired, setSessionExpired] = React.useState(false);
  const setUser = useSetRecoilState(UserState);
  const setSession = useSetRecoilState(SessionState);
  const [property_ids, setPropertyIds] = useRecoilState(PropertyIds);
  const setSelectedPropertyId = useSetRecoilState(SelectedPropertyId);
  const setSelectedPropertyIds = useSetRecoilState(SelectedPropertyIds);
  const setFetchedPropertyHolders = useSetRecoilState(FetchedPropertyHolders);
  const setInvestmentUnreadCount = useSetRecoilState(investmentUnreadCount);
  const setAssessmentUnreadCount = useSetRecoilState(assessmentUnreadCount);
  const setHasUnread = useSetRecoilState(HasUnreadAnnouncementState);
  const setBackendLoaded = useSetRecoilState(BackendLoadedState);
  const setPropertyHoldersState = useRecoilCallback(
    ({ set }) =>
      (propertyHolders: PropertyHolder[]) => {
        propertyHolders.forEach((propertyHolder) => {
          set(PropertyHoldersState(propertyHolder.id), propertyHolder);
        });
      },
    []
  );

  const require_loading = useRecoilValue(RequireLoadingFromBackendState);
  // const user = useRecoilValue(UserState);
  // const name = user ? `${user.family_name} ${user.first_name}` : "名前未設定";

  const setUserDataFirstRegisteredStatus = useSetRecoilState(UserDataFirstRegisteredStatusState);
  // const isRegisteredAccount = userDataFirstRegisteredStatus === RegisteredStatusEnum.Account;

  // 遷移先画面と現在のユーザ情報入力ステータスから初期STEP入力画面を表示するかどうか判定する
  // const isDisplayFirstStep = () => {
  //   switch (Routes.find((route) => route.url_path === location.pathname)?.required_register) {
  //     case RegisteredStatusEnum.Registration:
  //       return false;

  //     case RegisteredStatusEnum.Account:
  //       return userDataFirstRegisteredStatus === RegisteredStatusEnum.Registration;

  //     case RegisteredStatusEnum.Property:
  //       return userDataFirstRegisteredStatus !== RegisteredStatusEnum.Property;

  //     default:
  //       return false;
  //   }
  // };

  const InitialLoading = async () => {
    const api = PropallyApiFactory();

    try {
      /** User */
      {
        const user = await api.v1UserGet({
          withCredentials: true,
        });
        setUser(user.data as unknown as User);
        // Sentry送信時にユーザー情報も付与
        Sentry.configureScope((scope) => {
          scope.setUser({ id: String(user.data.id) });
        });

        const registeredStatus = await api.v1UserDataFirstRegisteredStatusGet({ withCredentials: true });
        setUserDataFirstRegisteredStatus(registeredStatus.data.status);

        // 未読アナウンス有無の更新
        setHasUnread(user.data.has_unread_announcements ?? false);
      }

      /**  Property  */
      {
        const properties = await api.v1PropertiesGet({
          withCredentials: true,
        });
        console.log("in RecoilRoot.tsx", properties.data);
        const property_holders = await Promise.all(
          properties.data.map(async (e) => {
            let lease_contracts = null;
            let building_room = null;
            if (e.property_category === "区分マンション") {
              lease_contracts = await api.v1PropertiesPropertyIdLeaseContractsGet(e.id, {
                withCredentials: true,
              });
            }

            if (e.property_category === "一棟物件") {
              building_room = await api.v1PropertiesPropertyIdBuildingRoomsGet(e.id, {
                withCredentials: true,
              });
            }
            const simulation_config = await api.v1PropertiesPropertyIdSimulationConfigsGet(e.id, {
              withCredentials: true,
            });

            let image;
            if (e.images.length !== 0) {
              const image_url = await api.v1PropertiesPropertyIdImagesImageIdUrlGet(
                e.id,
                e.images[e.images.length - 1].id,
                {
                  withCredentials: true,
                }
              );
              image = {
                image: e.images[e.images.length - 1],
                url: image_url.data.url,
              };
            }

            // 初回読み込みの401/503を判別できるよう、HTTPエラーを文字列へ変換せず受け取る。
            const monthly_cashflow_results = await api.v1PropertiesPropertyIdMonthlyCashflowResultsGet(e.id, {
              withCredentials: true,
            });

            return {
              id: e.id,
              property: e,
              images: image ? [image] : [],
              lease_contracts: lease_contracts ? lease_contracts.data : [],
              building_room: building_room ? building_room.data : [],
              simulation_configs: simulation_config.data,
              cover_image_url: image ? image.url : NoImage,
              user_input_monthly_cashflow_results: monthly_cashflow_results.data,
            };
          })
        );

        /** Proposal Unread Count */
        {
          const investmentUnreadCount = await api.v1InvestmentProposalUnreadCountGet({
            withCredentials: true,
          });
          setInvestmentUnreadCount(investmentUnreadCount.data as { unread_count: number });

          const assessmentUnreadCount = await api.v1AssessmentProposalUnreadCountGet({
            withCredentials: true,
          });
          setAssessmentUnreadCount(assessmentUnreadCount.data as { unread_count: number });
        }

        setFetchedPropertyHolders(property_holders);
        setPropertyHoldersState(property_holders);
        const ids = properties.data.map((element) => {
          return element.id;
        });

        if (!_.isEqual(property_ids, ids)) {
          setPropertyIds(ids);
          const owned_property_ids = properties.data
            .filter((element) => {
              return (
                element.property_type === PROPERTY_TYPE_VALUES.owned ||
                element.property_type === PROPERTY_TYPE_VALUES.owned_sample
              );
            })
            .map((element) => {
              return element.id;
            });
          setSelectedPropertyIds(owned_property_ids);
          setSelectedPropertyId(owned_property_ids[0]);
        }
      }

      /** 物件一時保存情報
       *  TODO: 初期STEP入力画面の場合のみ、物件一覧を経由せず、直接物件登録画面に遷移するため、ここで物件一時保存情報を取得しローカルストレージにセットしておく
       * */
      // if (isDisplayFirstStep) {
      //   api.v1PropertyTemporaryGet({ withCredentials: true }).then((res) => {
      //     const property_temporary = res.data;

      //     if (_.isEmpty(property_temporary)) {
      //       removePropertyTemporaryForm();
      //       removeStoragePropertyTemporaryStep();
      //     } else {
      //       setStoragePropertyTemporaryForm(res.data.property_form);
      //       setStoragePropertyTemporaryStep(res.data.active_step_number);
      //     }
      //   });
      // }

      // Success
      // enqueueSnackbar("データの読み込みに成功しました", {
      //   anchorOrigin: { vertical: "bottom", horizontal: "right" },
      // });
      loadedOnce.current = true;
      setLoadFailure(null);
      setBackendLoaded(true);
    } catch (e) {
      if (e.response?.status === 401) {
        setSession({ logged_in: false });
        setSessionExpired(true);
      } else if (!loadedOnce.current) setLoadFailure(e.response?.status === 503 ? "maintenance" : "network");
      else NotifyNetworkError(enqueueSnackbar, closeSnackbar);
      setBackendLoaded(true);
    } finally {
      setInitialLoading(false);
    }
  };

  React.useEffect(() => {
    InitialLoading();
  }, [require_loading]);

  if (sessionExpired) return <Redirect to="/login" />;
  if (loadFailure) return <AppLoadError kind={loadFailure} />;
  if (initialLoading) return <Loading />;

  // if (isDisplayFirstStep()) {
  //   // 初回情報登録必要な画面でアカウント未登録または物件情報未登録の場合に表示
  //   return (
  //     <Box bgcolor="primary.main" p={3} borderRadius={8} color="white">
  //       <Box mb={3}>
  //         <Typography variant="h1">{`ようこそ ${name} さん！`}</Typography>
  //       </Box>
  //       <Box mb={3}>
  //         <Typography variant="body1">
  //           Propally for Ownersでは、物件の収支管理・シミュレーションが可能です。
  //           <br />
  //           また一定の実績を持つ不動産エージェントより、カスタムメイドなオファーを受けることが出来ます。(全て無料でご利用頂けます)
  //           <br />
  //           <br />
  //           既に不動産投資をしている方は、お持ちの物件情報を登録して収支管理を見える化しましょう。
  //           <br />
  //           初めての方も、気になる物件を登録頂ければ精確なシミュレーションを行え、意思決定の判断材料となります。
  //           <br />
  //           <br />
  //           また、あなたの入力情報を元に『<strong>価値のあるオファーが可能</strong>
  //           』と判断したエージェントより、新規の投資オファーや保有物件の価格査定・買取オファーをお届けしております。
  //           <br />
  //           詳細を聞きたい場合はオファーを承認し、実際にエージェントと会話してみましょう。
  //         </Typography>
  //       </Box>
  //       <Box mt={6} justifyContent="center" display="flex">
  //         <KeyboardArrowDownIcon />
  //         <Typography variant="body2">STEP1 : プロフィールを登録</Typography>
  //       </Box>
  //       <Box justifyContent="center" display="flex">
  //         <Button
  //           type="submit"
  //           variant="contained"
  //           size="large"
  //           component={Link}
  //           to="/first_account"
  //           disabled={Boolean(isRegisteredAccount)}
  //         >
  //           アカウント情報を登録
  //         </Button>
  //       </Box>
  //       <Box mt={3} justifyContent="center" display="flex">
  //         <KeyboardArrowDownIcon />
  //         <Typography variant="body2">STEP2 : 物件を登録</Typography>
  //       </Box>
  //       <Box justifyContent="center" display="flex">
  //         <Button
  //           type="submit"
  //           variant="contained"
  //           size="large"
  //           component={Link}
  //           to="/first_add_new_property"
  //           disabled={!isRegisteredAccount}
  //         >
  //           保有物件・検討中物件を登録
  //         </Button>
  //       </Box>
  //     </Box>
  //   );
  // }
  return (
    <>
      <DiagnosisRedirector />
      <NotifyInflowChannelTracker />
      <AppBadgeSync />
      <ConsentModals />
      {props.children}
    </>
  );
};

// 未読件数とOSバッジの同期（認証後のRecoil文脈内で常時稼働）
// MEMO: 表示されるかは端末の仕様による
//       例)Pixel（標準のPixel Launcher）では数値バッジは表示されず、未処理の通知があることを示す“ドット”のみがサポートされている
const AppBadgeSync: React.FC = () => {
  const inv = useRecoilValue(investmentUnreadCount);
  const ass = useRecoilValue(assessmentUnreadCount);

  React.useEffect(() => {
    const total = (inv?.unread_count ?? 0) + (ass?.unread_count ?? 0);
    if (total > 0) {
      setAppBadgeCount(total);
    } else {
      clearAppBadge();
    }
  }, [inv?.unread_count, ass?.unread_count]);

  return null;
};

const DiagnosisRedirector: React.FC = () => {
  const user = useRecoilValue(UserState);
  const location = useLocation();
  const history = useHistory();

  React.useEffect(() => {
    if (!user) return;
    if (user.is_diagnosis_complete !== false) return;
    if (user.data_first_registered_status === RegisteredStatusEnum.Registration) return;
    if (location.pathname === "/diagnosis" || location.pathname === "/first_account") return;
    history.replace("/diagnosis");
  }, [user, location.pathname, history]);

  return null;
};

// MEMO: 以下は流入チャネルとユーザーの紐付けを加速するための実装
//       処理が重くなる可能性があるので、十分データが集まった後は削除予定
const TRACKED_PATH_PREFIXES = [
  "/mypage",
  "/offer",
  "/investment",
  "/assessment",
  "/management/own",
  "/management/consideration",
  "/agent_search",
  "/settings",
  "/journal",
] as const;

const shouldTrackPath = (pathname: string): boolean => {
  return TRACKED_PATH_PREFIXES.some((basePath) => pathname === basePath || pathname.startsWith(`${basePath}/`));
};

const NotifyInflowChannelTracker: React.FC = () => {
  const location = useLocation();
  const user = useRecoilValue(UserState);
  const lastSentPathRef = React.useRef<string | null>(null);
  const { pathname } = location;

  React.useEffect(() => {
    if (!user?.id) return;
    if (!shouldTrackPath(pathname)) return;
    if (lastSentPathRef.current === pathname) return;

    lastSentPathRef.current = pathname;
    sendEventToAppMeasurementTool(AppEventTokenEnum.NotifyInflowChannel, { user_id: user.id });
  }, [pathname, user?.id]);

  return null;
};
